/**
 * Admin API service for feature and assignment management
 */

import { api } from '@services/api';
import { API_PATHS } from '@utils/constants';
import { AxiosError } from 'axios';
import {
  FeatureListSchema,
  FeatureSchema,
  AssignmentListSchema,
  AssignmentSchema,
  AssignmentWithDepsResponseSchema,
  AuditLogListSchema,
  BaseImageInfoSchema,
  ImageCacheListSchema,
  SyncResultSchema,
  FeaturePackListSchema,
  FeaturePackSummarySchema,
  FeaturePackDetailSchema,
  PackItemSchema,
  PackItemWithDepsResponseSchema,
  LinkedProjectSchema,
  BulkLinkResponseSchema,
  RuntimeConfigListSchema,
  RuntimeConfigEntrySchema,
  CaUploadResponseSchema,
  MissingRuntimeConfigListSchema,
} from '../schemas/admin.schemas';
import type {
  Feature,
  Assignment,
  AssignmentWithDepsResponse,
  AuditLogEntry,
  BaseImageInfo,
  ImageCacheEntry,
  SyncResult,
  FeaturePackSummary,
  FeaturePackDetail,
  PackItem,
  LinkedProject,
  BulkLinkResponse,
  RuntimeConfigEntry,
  RuntimeConfigList,
  CaUploadResponse,
  MissingRuntimeConfigItem,
} from '../schemas/admin.schemas';

export interface RuntimeConfigCtx {
  projectId?: string;
  packId?: string;
}

const ctxParams = (ctx?: RuntimeConfigCtx): string => {
  const p = new URLSearchParams();
  if (ctx?.projectId) p.set('project_id', ctx.projectId);
  if (ctx?.packId) p.set('pack_id', ctx.packId);
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const adminApi = {
  listFeatures: async (includeInactive = false): Promise<Feature[]> => {
    const params = includeInactive ? '?include_inactive=true' : '';
    const response = await api.get(`${API_PATHS.ADMIN_FEATURES}${params}`);
    const data: unknown = response.data;
    return FeatureListSchema.parse(data).features;
  },

  createFeature: async (
    feature: Omit<Feature, 'id' | 'created_at' | 'updated_at' | 'is_active'>,
  ): Promise<Feature> => {
    const response = await api.post(API_PATHS.ADMIN_FEATURES, feature);
    const data: unknown = response.data;
    return FeatureSchema.parse(data);
  },

  updateFeature: async (
    featureId: string,
    updates: Record<string, unknown>,
  ): Promise<Feature> => {
    const response = await api.put(
      `${API_PATHS.ADMIN_FEATURES}/${featureId}`,
      updates,
    );
    const data: unknown = response.data;
    return FeatureSchema.parse(data);
  },

  deleteFeature: async (featureId: string): Promise<void> => {
    await api.delete(`${API_PATHS.ADMIN_FEATURES}/${featureId}`);
  },

  listProjectFeatures: async (projectId: string): Promise<Assignment[]> => {
    const response = await api.get(
      `${API_PATHS.ADMIN_PROJECTS}/${projectId}/features`,
    );
    const data: unknown = response.data;
    return AssignmentListSchema.parse(data).assignments;
  },

  assignFeature: async (
    projectId: string,
    featureId: string,
    projectName: string,
    options?: Record<string, unknown>,
  ): Promise<AssignmentWithDepsResponse> => {
    const response = await api.post(
      `${API_PATHS.ADMIN_PROJECTS}/${projectId}/features`,
      {
        project_id: projectId,
        project_name: projectName,
        feature_id: featureId,
        ...(options ? { options } : {}),
      },
    );
    const data: unknown = response.data;
    return AssignmentWithDepsResponseSchema.parse(data);
  },

  updateAssignment: async (
    assignmentId: string,
    options: Record<string, unknown> | null,
  ): Promise<Assignment> => {
    const response = await api.put(
      `${API_PATHS.ADMIN_ASSIGNMENTS}/${assignmentId}`,
      { options },
    );
    const data: unknown = response.data;
    return AssignmentSchema.parse(data);
  },

  removeAssignment: async (assignmentId: string): Promise<void> => {
    await api.delete(`${API_PATHS.ADMIN_ASSIGNMENTS}/${assignmentId}`);
  },

  getAuditLog: async (projectId: string): Promise<AuditLogEntry[]> => {
    const response = await api.get(
      `${API_PATHS.ADMIN_PROJECTS}/${projectId}/audit-log`,
    );
    const data: unknown = response.data;
    return AuditLogListSchema.parse(data).entries;
  },

  syncFeatures: async (): Promise<SyncResult> => {
    const response = await api.post(`${API_PATHS.ADMIN_FEATURES}/sync`);
    const data: unknown = response.data;
    return SyncResultSchema.parse(data);
  },

  getBaseImageInfo: async (): Promise<BaseImageInfo> => {
    const response = await api.get(API_PATHS.ADMIN_BASE_IMAGE);
    const data: unknown = response.data;
    return BaseImageInfoSchema.parse(data);
  },

  listImageCache: async (): Promise<ImageCacheEntry[]> => {
    const response = await api.get(API_PATHS.ADMIN_IMAGE_CACHE);
    const data: unknown = response.data;
    return ImageCacheListSchema.parse(data).images;
  },

  deleteImageCacheEntry: async (cacheId: string): Promise<void> => {
    await api.delete(`${API_PATHS.ADMIN_IMAGE_CACHE}/${cacheId}`);
  },

  listPacks: async (): Promise<FeaturePackSummary[]> => {
    const response = await api.get(API_PATHS.ADMIN_PACKS);
    const data: unknown = response.data;
    return FeaturePackListSchema.parse(data).packs;
  },

  getPack: async (packId: string): Promise<FeaturePackDetail> => {
    const response = await api.get(`${API_PATHS.ADMIN_PACKS}/${packId}`);
    const data: unknown = response.data;
    return FeaturePackDetailSchema.parse(data);
  },

  createPack: async (
    name: string,
    displayName: string,
    description?: string,
  ): Promise<FeaturePackSummary> => {
    const response = await api.post(API_PATHS.ADMIN_PACKS, {
      name,
      display_name: displayName,
      ...(description !== undefined ? { description } : {}),
    });
    const data: unknown = response.data;
    return FeaturePackSummarySchema.parse(data);
  },

  updatePack: async (
    packId: string,
    displayName?: string,
    description?: string,
  ): Promise<FeaturePackSummary> => {
    const response = await api.put(`${API_PATHS.ADMIN_PACKS}/${packId}`, {
      ...(displayName !== undefined ? { display_name: displayName } : {}),
      ...(description !== undefined ? { description } : {}),
    });
    const data: unknown = response.data;
    return FeaturePackSummarySchema.parse(data);
  },

  deletePack: async (packId: string): Promise<void> => {
    await api.delete(`${API_PATHS.ADMIN_PACKS}/${packId}`);
  },

  copyPack: async (
    packId: string,
    name: string,
    displayName: string,
  ): Promise<FeaturePackSummary> => {
    const response = await api.post(
      `${API_PATHS.ADMIN_PACKS}/${packId}/copy`,
      { name, display_name: displayName },
    );
    const data: unknown = response.data;
    return FeaturePackSummarySchema.parse(data);
  },

  addPackItem: async (
    packId: string,
    featureId: string,
    options?: Record<string, unknown>,
  ): Promise<PackItem> => {
    const response = await api.post(
      `${API_PATHS.ADMIN_PACKS}/${packId}/items`,
      {
        feature_id: featureId,
        ...(options !== undefined ? { options } : {}),
      },
    );
    const data: unknown = response.data;
    const parsed = PackItemWithDepsResponseSchema.parse(data);
    return parsed.item;
  },

  updatePackItem: async (
    packId: string,
    itemId: string,
    options: Record<string, unknown>,
  ): Promise<PackItem> => {
    const response = await api.put(
      `${API_PATHS.ADMIN_PACKS}/${packId}/items/${itemId}`,
      { options },
    );
    const data: unknown = response.data;
    return PackItemSchema.parse(data);
  },

  removePackItem: async (packId: string, itemId: string): Promise<void> => {
    await api.delete(`${API_PATHS.ADMIN_PACKS}/${packId}/items/${itemId}`);
  },

  linkProjectToPack: async (
    projectId: string,
    packId: string,
    projectName: string,
  ): Promise<void> => {
    await api.post(
      `${API_PATHS.ADMIN_PROJECTS}/${projectId}/pack`,
      { pack_id: packId, project_name: projectName },
    );
  },

  detachProjectFromPack: async (projectId: string): Promise<void> => {
    await api.delete(
      `${API_PATHS.ADMIN_PROJECTS}/${projectId}/pack`,
    );
  },

  getProjectPack: async (
    projectId: string,
  ): Promise<LinkedProject | null> => {
    try {
      const response = await api.get(
        `${API_PATHS.ADMIN_PROJECTS}/${projectId}/pack`,
      );
      const data: unknown = response.data;
      return LinkedProjectSchema.parse(data);
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  bulkLinkProjects: async (
    packId: string,
    projects: Array<{ project_id: string; project_name: string }>,
  ): Promise<BulkLinkResponse> => {
    const response = await api.post(
      `${API_PATHS.ADMIN_PACKS}/${packId}/link-projects`,
      { projects },
    );
    const data: unknown = response.data;
    return BulkLinkResponseSchema.parse(data);
  },

  getRuntimeConfig: async (
    featureId: string,
    ctx?: RuntimeConfigCtx,
  ): Promise<RuntimeConfigList> => {
    const response = await api.get(
      `${API_PATHS.ADMIN_FEATURES}/${featureId}/runtime-config${ctxParams(ctx)}`,
    );
    const data: unknown = response.data;
    return RuntimeConfigListSchema.parse(data);
  },

  putRuntimeConfigValue: async (
    featureId: string,
    kind: 'file' | 'env',
    name: string,
    payload: {
      value: string;
      target_path?: string;
      file_mode?: string;
      secret?: boolean;
    },
    ctx?: RuntimeConfigCtx,
  ): Promise<RuntimeConfigEntry> => {
    const response = await api.put(
      `${API_PATHS.ADMIN_FEATURES}/${featureId}/runtime-config/${kind}/${encodeURIComponent(name)}${ctxParams(ctx)}`,
      payload,
    );
    const data: unknown = response.data;
    return RuntimeConfigEntrySchema.parse(data);
  },

  deleteRuntimeConfigValue: async (
    featureId: string,
    kind: 'file' | 'env',
    name: string,
    ctx?: RuntimeConfigCtx,
  ): Promise<void> => {
    await api.delete(
      `${API_PATHS.ADMIN_FEATURES}/${featureId}/runtime-config/${kind}/${encodeURIComponent(name)}${ctxParams(ctx)}`,
    );
  },

  parseCaUpload: async (
    featureId: string,
    name: string,
    ctx: RuntimeConfigCtx | undefined,
    body: { fileB64: string; password: string | null; existing: string },
  ): Promise<CaUploadResponse> => {
    const response = await api.post(
      `${API_PATHS.ADMIN_FEATURES}/${featureId}/runtime-config/file/${encodeURIComponent(name)}/parse-ca${ctxParams(ctx)}`,
      { file_b64: body.fileB64, password: body.password, existing: body.existing },
    );
    const data: unknown = response.data;
    return CaUploadResponseSchema.parse(data);
  },

  revertRuntimeConfig: async (
    featureId: string,
    ctx: RuntimeConfigCtx,
  ): Promise<void> => {
    await api.post(
      `${API_PATHS.ADMIN_FEATURES}/${featureId}/runtime-config/revert${ctxParams(ctx)}`,
    );
  },

  getMissingRuntimeConfig: async (
    ctx: { packId: string } | { projectId: string },
  ): Promise<MissingRuntimeConfigItem[]> => {
    const base = 'packId' in ctx
      ? `${API_PATHS.ADMIN_PACKS}/${ctx.packId}`
      : `${API_PATHS.ADMIN_PROJECTS}/${ctx.projectId}`;
    const response = await api.get(`${base}/runtime-config/missing`);
    const data: unknown = response.data;
    return MissingRuntimeConfigListSchema.parse(data).items;
  },
};
